// Gestión de contraseñas temporales con timeout
export interface PasswordSession {
  password: string;
  timestamp: number;
  expiresIn: number; // milisegundos
}

class PasswordManager {
  private static readonly STORAGE_KEY = 'cuerda_temp_password';
  private static readonly DEFAULT_TIMEOUT = 600000; // 10 minutos en milisegundos

  /**
   * Guarda la contraseña temporalmente
   */
  static setTemporaryPassword(password: string, timeoutMs: number = this.DEFAULT_TIMEOUT): void {
    const session: PasswordSession = {
      password,
      timestamp: Date.now(),
      expiresIn: timeoutMs,
    };
    
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(session));
    
    // Auto-limpiar después del timeout
    setTimeout(() => {
      this.clearTemporaryPassword();
    }, timeoutMs);
  }

  /**
   * Obtiene la contraseña si aún es válida
   */
  static getTemporaryPassword(): string | null {
    try {
      const sessionData = sessionStorage.getItem(this.STORAGE_KEY);
      if (!sessionData) return null;

      const session: PasswordSession = JSON.parse(sessionData);
      const now = Date.now();
      
      // Verificar si no ha expirado
      if (now - session.timestamp > session.expiresIn) {
        this.clearTemporaryPassword();
        return null;
      }

      return session.password;
    } catch (error) {
      this.clearTemporaryPassword();
      return null;
    }
  }

  /**
   * Verifica si tenemos una contraseña válida
   */
  static hasValidPassword(): boolean {
    return this.getTemporaryPassword() !== null;
  }

  /**
   * Limpia la contraseña temporal
   */
  static clearTemporaryPassword(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Obtiene el tiempo restante en segundos
   */
  static getTimeRemaining(): number {
    try {
      const sessionData = sessionStorage.getItem(this.STORAGE_KEY);
      if (!sessionData) return 0;

      const session: PasswordSession = JSON.parse(sessionData);
      const now = Date.now();
      const elapsed = now - session.timestamp;
      const remaining = Math.max(0, session.expiresIn - elapsed);
      
      return Math.ceil(remaining / 1000); // convertir a segundos
    } catch (error) {
      return 0;
    }
  }

  /**
   * Extiende el tiempo de la sesión actual
   */
  static extendSession(additionalTimeMs: number = this.DEFAULT_TIMEOUT): boolean {
    const currentPassword = this.getTemporaryPassword();
    if (!currentPassword) return false;

    this.setTemporaryPassword(currentPassword, additionalTimeMs);
    return true;
  }
}

export default PasswordManager; 